import dayjs from 'dayjs';
import { Form, Spin } from 'antd';
import _, { isEmpty } from 'lodash';
import LightTable from './LightTable';
import { useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import CardAccordion from 'components/CardAccordion/CardAccordion';
import { useSendControlDataMutation } from 'store/features/api/apiSlice';
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { formatNumber } from '../../lease-record-management-v9-new-version/utils/helperFunc';

const LeaseV9NewGeneralLedgerView = forwardRef((_props, ref) => {
	const form = Form.useFormInstance();
	const { state } = useLocation();
	const [sendControlDataForShowAll, { data: generalLedgerData, isLoading: isSendControlDataLoading }] = useSendControlDataMutation();
	const [previousPayload, setPreviousPayload] = useState(null);

	const loadData = async () => {
		const payloadData = {
			leaseId: form.getFieldValue('leaseId'),
		};

		const convertJson = JSON.stringify(payloadData);
		const convertBase64 = btoa(convertJson);

		const formattedPayload = {
			data: convertBase64,
			processCode: state?.processCode,
			scriptName: 'lease-controller-schedule-get-findata',
			payloadData: payloadData,
		};

		// Check if current payload is same as previous to avoid unnecessary API call
		const currentPayloadStr = JSON.stringify(formattedPayload);
		const previousPayloadStr = previousPayload ? JSON.stringify(previousPayload) : null;

		if (previousPayloadStr !== currentPayloadStr) {
			await sendControlDataForShowAll(formattedPayload);
			setPreviousPayload(formattedPayload);
		}
	};

	useImperativeHandle(ref, () => ({
		loadData,
	}));
	// useEffect(() => {
	// 	loadData();
	// }, []);

	const columnData = useMemo(
		() => [
			{
				id: 'recordCreationDateTime',
				width: '5%',
				accessor: (data) => {
					return dayjs(data?.latestProcessGroup?.creationDateTime?.dateString).format('YYYY-MM-DD');
				},
				Header: 'Reference Date',
				cellSx: { fontSize: '13px' },
			},
			{
				id: 'description',
				width: '5%',
				accessor: (data) => data?.description,
				cellAlign: 'left',
				Header: 'Description',
				cellSx: { fontSize: '13px' },
			},
			{
				id: 'processStatus',
				width: '5%',
				accessor: (data) => data?.latestProcessGroup?.processStatus,
				Header: 'Type',
				cellSx: { fontSize: '13px' },
			},

			{
				id: 'total',
				width: '5%',
				Header: 'Amount',
				accessor: (data) => {
					const debit = data?.debit ? parseFloat(data?.debit) : 0;
					const credit = data?.credit ? parseFloat(data?.credit) : 0;
					const total = isNaN(debit) ? 0 : debit - (isNaN(credit) ? 0 : credit);

					return total !== 0 ? (total > 0 ? formatNumber(total, 2) : '(' + formatNumber(Math.abs(total), 2) + ')') : '';
				},
			},
			{
				id: 'comulativeAmount',
				width: '5%',
				accessor: 'comulativeAmount',
				Header: 'Cumulative Amount',
				cellSx: { fontSize: '13px' },
			},
		],
		[]
	);

	const headSequence = [
		'RoU Asset - Initial Recognition',
		'RoU Asset - Modification',
		'RoU Asset - Pre-payment',
		'RoU Asset - Upfront Payment',
		'RoU Asset - Indirect Costs',
		'RoU Asset - Cash Incentive',
		'RoU Asset - Knock Off',
		'RoU Asset - Prepayments',
		'Security Deposit',
		'Security Deposit - Security Deposit',
		'Lease Liability - Initial Recognition',
		'Lease Liability - Modification',
		'Lease Liability - Payment',
		'Lease Liability - Interest',
		'Interest Expense',
		'Depreciation - RoU',
		'Accum Depreciation - RoU',
		'Accum Depreciation - RoU - Knock Off',
		'A/P Control Ledger - Pre-payment',
		'A/P Control Ledger - Upfront Payment',
		'A/P Control Ledger - Indirect Costs',
		'A/P Control Ledger - Security Deposit',
		'A/P Control Ledger - Payment',
		'A/P Control Ledger - Prepayments',
		'A/R Control Ledger - Cash Incentive',
		'Interest Expense - Interest',
		'Depreciation - RoU - Depreciation',
		'Accum Depreciation - RoU - Depreciation',
	];

	const groupDataByHead = (data) => {
		if (!Array.isArray(data)) return null;

		// First, group the data by the 'head' field
		const grouped = _.groupBy(data, 'head');

		// Create an ordered result based on headSequence
		const result = headSequence
			.filter((head) => grouped[head]) // Only include heads that exist in the data
			.map((head) => {
				const values = grouped[head];
				let runningTotal = 0;

				// Calculate running totals and format data
				const indexedValues = values.map((item, index) => {
					const { head, ...rest } = item;

					// Calculate the value for this row (debit - credit)
					const debit = parseFloat(item.debit || 0);
					const credit = parseFloat(item.credit || 0);
					const rowValue = debit - credit;

					// Update running total
					runningTotal += rowValue;

					// Format the cumulative amount
					const formattedCumulative =
						runningTotal !== 0
							? runningTotal > 0
								? formatNumber(runningTotal, 2)
								: '(' + formatNumber(Math.abs(runningTotal), 2) + ')'
							: '';

					return {
						...rest,
						index,
						comulativeAmount: formattedCumulative,
					};
				});

				return {
					head,
					value: indexedValues,
				};
			});

		return result;
	};

	const filteredData = Array.isArray(generalLedgerData?.responseData)
		? generalLedgerData?.responseData?.filter((item) => {
				return item?.cancelStatus?.toLowerCase() !== 'cancelled';
			})
		: [];

	const groupedData = useMemo(() => groupDataByHead(filteredData), [filteredData]);

	return (
		<CardAccordion
			title="General Ledger View"
			darkTitle={true}
			sx={{
				mt: 2,
				mb: 2,
			}}
			customHeaderSX={{
				backgroundColor: '#e1f0fb !important',
				p: '10px',
				fontSize: '16px !important',
			}}
			typographySX={{ fontSize: '16px !important' }}
		>
			<Box>
				<Spin spinning={isSendControlDataLoading || groupedData === null} style={{ maxHeight: '100%' }}>
					{generalLedgerData?.responseData?.length !== 0 ? (
						<div className="lease-details-tables">
							{Array.isArray(groupedData) && groupedData.length > 0
								? groupedData.map((group, groupIndex) => (
										<div key={groupIndex}>
											<Typography
												variant="h6"
												sx={{ fontSize: '16px !important', fontWeight: 500, color: '#000000', mb: '10px', pl: '11px' }}
											>
												{group.head}
											</Typography>
											<div className="head-group-table" style={{ marginBottom: '30px' }}>
												<LightTable
													data={isEmpty(group.value) ? [] : group.value}
													columns={columnData}
													emptyMessage="No data available for this head"
													headerCellSx={{
														backgroundColor: '#EDF7FF !important',
														color: 'black',
														fontWeight: 500,
														height: '3.5rem',
														padding: '0.75rem',
													}}
													tableContainerSx={{
														border: '1px solid #b5b5b5',
														borderRadius: '4px',
														fontsize: '13px',
														// mb: 2,
														'& .MuiTableHead-root th': {
															backgroundColor: '#EDF7FF !important',
															color: '#000000',
														},
													}}
												/>

												{/* <Divider /> */}
											</div>
											{/* </CardAccordion> */}
										</div>
									))
								: generalLedgerData?.length === 0 &&
									groupedData?.length === 0 &&
									!isSendControlDataLoading && (
										<div style={{ textAlign: 'center', margin: '20px' }}>
											<Typography>No lease data available</Typography>
										</div>
									)}
						</div>
					) : (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
							}}
						>
							No Ledger Available Right Now
						</Box>
					)}
				</Spin>
			</Box>
		</CardAccordion>
	);
});

LeaseV9NewGeneralLedgerView.displayName = 'LeaseDetailsShowAll';

export default LeaseV9NewGeneralLedgerView;
